/** @prettier */

import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { loadTreatment } from '@surfline/web-common/dist/features/redux';
import RailIcon from '@surfline/quiver-react/dist/icons/RailIcon';
import CamTimeout from '@surfline/quiver-react/dist/CamTimeout';
import AdPlayer from '@surfline/quiver-react/dist/AdPlayer';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import CloseIcon from '@surfline/quiver-react/dist/CloseIcon';
import { useDeviceSize } from '@surfline/quiver-react/dist/hooks/useDeviceSize';

import Cam from './Cam';
import { getMultiCam, getActiveCollectionId } from '../../selectors/multiCam';
import CamControls from './CamControls';
import { useToggle } from '../../hooks/useToggle';
import { SL_CAM_TIMEOUT, SL_MULTI_CAM_ADS } from '../../common/treatments';
import { useKeyPress } from '../../hooks/useKeyPress';
import { changePrimarySpotIndex } from '../../actions/multiCam';
import { getPremiumStatus, getUserType, IS_BASIC, ANONYMOUS } from '../../selectors/user';
import MultiCamPaywall from '../MultiCamPaywall/MultiCamPaywall';
import createAccessibleOnClick, { BTN } from '../../utils/createAccessibleOnClick';
import { clickedSubscribeCTA } from '../../actions/segmentEvents';
import config from '../../config';
import { getQueryParams } from '../../selectors/router';
import { useAdTimer } from './hooks/useAdTimer';
import { useCamTimeout } from './hooks/useCamTimeout';
import { createFillers, multiCamContainerClass, multiCamClass } from './utils';
import CamHeaderCTA from '../CamHeaderCTA';
import MultiCamRegWall from '../MultiCamRegWall';
import { useSaveMultiCamState } from './hooks/useSaveMultiCamState';

const useRedux = () => {
  const dispatch = useDispatch();
  const showCamTimeout = dispatch(loadTreatment(SL_CAM_TIMEOUT)) !== 'off';
  const showAdsTreatment = dispatch(loadTreatment(SL_MULTI_CAM_ADS)) === 'on';
  const doChangePrimarySpotIndex = useCallback(
    camIndex => dispatch(changePrimarySpotIndex(camIndex)),
    [dispatch]
  );
  const doClickedSubscribeCTA = () =>
    dispatch(
      clickedSubscribeCTA({
        ctaLocation: 'mid roll',
        pageName: 'multi-cam',
      })
    );
  return { showCamTimeout, doChangePrimarySpotIndex, doClickedSubscribeCTA, showAdsTreatment };
};

const MultiCam = React.memo(
  ({
    canMountCams,
    isFullscreen,
    toggleFullscreen,
    timeoutTime,
    toggleRightRail,
    rightRailIsOpen,
    setCompanionAdKey,
    children,
  }) => {
    /* Saved Multi Cam States */
    useSaveMultiCamState();

    /* Cam Container Ref */
    const camsRef = useRef();

    /** State */
    const { numCams, selectedSpots, primarySpotIndex } = useSelector(getMultiCam, shallowEqual);
    const isPremium = useSelector(getPremiumStatus);
    const userType = useSelector(getUserType);
    const { qaFlag } = useSelector(getQueryParams, shallowEqual);
    const activeCollectionId = useSelector(getActiveCollectionId);
    useDeviceSize();

    /** Actions */
    const {
      showCamTimeout,
      doChangePrimarySpotIndex,
      doClickedSubscribeCTA,
      showAdsTreatment,
    } = useRedux();

    /**
     * Memoized array of selected spot ID's `selectedSpots` should be immutable since it is in redux
     * so it is safe to use as a dependency in `useMemo`
     */
    const selectedSpotIds = useMemo(() => selectedSpots.map(spot => spot?._id), [selectedSpots]);

    /** Control the cams playing states */
    const [isPlaying, setPlaying, togglePlaying] = useToggle(isPremium);

    /** Control the ad playing states */
    const firstCamSpot = selectedSpots.find(spot => spot?.cameras?.length);

    const {
      isPlayingAd,
      onAdComplete,
      doPlayAd,
      showUpSell,
      onUpSellComplete,
      setAdPlaying,
    } = useAdTimer({
      isPremium,
      hasCam: !!firstCamSpot,
      userType,
      isPlaying,
      setCompanionAdKey,
      numCams,
    });

    const [timeout, resetTimeout] = useCamTimeout({
      showCamTimeout,
      setPlaying,
      isPlaying,
      selectedSpotIds,
      timeoutTime,
      hasCam: !!firstCamSpot,
    });

    const spotsToRender = useMemo(() => createFillers(selectedSpots, numCams), [
      numCams,
      selectedSpots,
    ]);

    // Allow users to select primary cam using 1, 2, 3, 4, etc...
    const primaryCamSelectionKeys = spotsToRender.map((_, index) => `${index + 1}`);
    useKeyPress(
      useCallback(key => doChangePrimarySpotIndex(parseInt(key, 10) - 1), [
        doChangePrimarySpotIndex,
      ]),
      ...primaryCamSelectionKeys
    );

    const showRegWall = userType === ANONYMOUS && showAdsTreatment && numCams !== 9;
    const showPaywall = showAdsTreatment ? !isPremium && numCams === 9 : !isPremium;
    const showAdPlayer =
      showAdsTreatment &&
      (isPlayingAd || showUpSell) &&
      !!firstCamSpot &&
      userType === IS_BASIC &&
      !showPaywall;

    /** If a free user changes collections, require them to click play again */
    useEffect(() => {
      if (userType === IS_BASIC && numCams !== 9 && activeCollectionId) {
        setAdPlaying(false);
        setPlaying(false);
      }
    }, [activeCollectionId, numCams, setAdPlaying, setPlaying, userType]);

    return (
      <div className="sl-multi-cam">
        <div
          className="sl-multi-cam__rail-toggle"
          {...createAccessibleOnClick(toggleRightRail, BTN)}
        >
          {rightRailIsOpen ? (
            <CloseIcon />
          ) : (
            <>
              <RailIcon />
              <span>spots</span>
            </>
          )}
        </div>
        <div className={multiCamContainerClass(showAdPlayer, showUpSell)}>
          <div
            ref={camsRef}
            className={multiCamClass(numCams, timeout, showCamTimeout, !isPremium)}
          >
            {spotsToRender.map((spot, camIndex) => (
              <Cam
                // Since we can have the same cam in different cam players we have to use an index as a key
                key={`${spot?._id || ''}${camIndex}`} // eslint-disable-line react/no-array-index-key
                spot={spot}
                isPlaying={isPlaying}
                camIndex={camIndex}
                primarySpotIndex={primarySpotIndex}
                onClick={() => doChangePrimarySpotIndex(camIndex)}
                canMountCams={canMountCams}
                isPremium={isPremium}
                camsRef={camsRef}
                showingAdPlayer={showAdPlayer}
                showingPaywall={showPaywall}
                showingRegwall={showRegWall}
                showingUpSell={showUpSell}
              />
            ))}
            <CamTimeout onClick={resetTimeout} />
            {showPaywall && <MultiCamPaywall />}
            {showRegWall && <MultiCamRegWall />}
            {showAdPlayer && (
              <AdPlayer
                onAdComplete={onAdComplete}
                onPlayAd={doPlayAd}
                isPlayingAd={isPlayingAd}
                camera={firstCamSpot.cameras[0]}
                playerId="sl-multi-cam-ad-player"
                spotId
                isPremium={isPremium}
                onClickUpSell={() => doClickedSubscribeCTA()}
                funnelUrl={config.funnelUrl}
                qaFlag={qaFlag}
                showUpSell={showUpSell}
                onUpSellComplete={onUpSellComplete}
              />
            )}
          </div>
          <CamControls
            isPlaying={isPlaying}
            isFullscreen={isFullscreen}
            togglePlaying={togglePlaying}
            toggleFullscreen={toggleFullscreen}
          />
        </div>
        {!isPremium && <CamHeaderCTA segmentProperties={{ location: 'button top - multicam' }} />}
        {children}
      </div>
    );
  }
);

MultiCam.propTypes = {
  canMountCams: PropTypes.bool,
  isFullscreen: PropTypes.bool,
  timeoutTime: PropTypes.number,
  toggleFullscreen: PropTypes.func.isRequired,
  toggleRightRail: PropTypes.func.isRequired,
  rightRailIsOpen: PropTypes.bool,
  setCompanionAdKey: PropTypes.func,
  children: PropTypes.element,
};

MultiCam.defaultProps = {
  canMountCams: true,
  isFullscreen: false,
  // 20 Minutes in milliseconds
  timeoutTime: 1200000,
  rightRailIsOpen: false,
  setCompanionAdKey: () => {},
  children: null,
};

export default MultiCam;
